'use client';

import { useEffect, type RefObject } from 'react';
import { stegaClean } from '@/lib/utils/stega';

type ChoiceControl = HTMLInputElement | HTMLSelectElement;

const CHOICE_CONTROLS = 'input[type="checkbox"], input[type="radio"], select';

/** The field a control submits under: a checkbox group's name ends in `[]`.
 *  Names and values are compared cleaned, since draft mode stega-encodes both. */
function fieldName(control: ChoiceControl): string {
    return stegaClean(control.name).replace(/\[\]$/, '');
}

function preselect(control: ChoiceControl, values: string[]) {
    if (control instanceof HTMLSelectElement) {
        const option = Array.from(control.options).find((item) => values.includes(stegaClean(item.value)));
        if (option) option.selected = true;
    } else if (values.includes(stegaClean(control.value))) {
        control.checked = true;
    }
}

/** Preselects the choices the URL hash names: `#leistung=innen` checks that
 *  box (or radio, or select option), `&` joins several pairs. Runs on mount and
 *  on every hash change. A plain `#id` — a tab, a section — carries no value
 *  and leaves the form alone. */
export function useHashPreselect(formRef: RefObject<HTMLFormElement | null>) {
    useEffect(() => {
        const apply = () => {
            const form = formRef.current;
            if (!form) return;

            const params = new URLSearchParams(window.location.hash.slice(1));

            form.querySelectorAll<ChoiceControl>(CHOICE_CONTROLS).forEach((control) => {
                const values = params.getAll(fieldName(control)).filter(Boolean);
                if (values.length > 0) preselect(control, values);
            });
        };

        apply();
        window.addEventListener('hashchange', apply);

        return () => window.removeEventListener('hashchange', apply);
    }, [formRef]);
}
